import { useEffect, useRef } from 'react';
import { Location, useLocation, useNavigationType } from 'react-router-dom';
import { isSamePageAnchorNavigation } from '@lib/isSamePageAnchorNavigation';

// How long a newly opened page keeps re-scrolling to its anchor while content loads in.
const ANCHOR_SETTLE_MS = 450;

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export const useAnchors = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const lastLocation = useRef<Location | null>(null);
  const previousLocation = useRef<Location | null>(null);

  useEffect(() => {
    // Keyed so StrictMode's repeated mount effect doesn't mistake this location for the previous one.
    if (lastLocation.current?.key !== location.key) {
      previousLocation.current = lastLocation.current;
      lastLocation.current = location;
    }
    const isFirstLoad = previousLocation.current === null;

    if (!location.hash || (!isFirstLoad && navigationType !== 'PUSH')) return undefined;

    const elementId = decodeURI(location.hash.slice(1));
    const element = document.getElementById(elementId);

    if (element && isSamePageAnchorNavigation(previousLocation.current, location)) {
      element.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      return undefined;
    }

    const scrollToElement = () => document.getElementById(elementId)?.scrollIntoView({ behavior: 'auto' });
    scrollToElement();
    const observer = new MutationObserver(scrollToElement);
    observer.observe(document, { childList: true, subtree: true });
    const timer = setTimeout(() => observer.disconnect(), ANCHOR_SETTLE_MS);

    return () => {
      observer.disconnect();
      clearTimeout(timer);
    };
  }, [location, navigationType]);
};
